import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from 'shared/lib/utils';

interface PopoverProps {
  /** Element the panel is anchored to (usually the field wrapper). */
  anchorRef: React.RefObject<HTMLElement>;
  /** Whether the panel is currently shown. */
  open: boolean;
  /** Optional ref onto the portalled panel, for outside-click checks. */
  panelRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  children: React.ReactNode;
}

interface PanelStyle {
  left: number;
  width: number;
  top: number;
  maxHeight: number;
}

// Gap between the anchor and the floating panel, and a soft cap on the panel
// height so it never runs off-screen.
const GAP = 4;
const MAX_PANEL_HEIGHT = 288;
const MIN_PANEL_HEIGHT = 160;
const VIEWPORT_MARGIN = 12;

/**
 * Renders `children` in a fixed-position layer portalled to `document.body`,
 * anchored to `anchorRef`. Because it escapes the normal DOM flow it is never
 * clipped by an ancestor's `overflow: hidden/auto` (e.g. cards or scroll
 * containers). The panel always opens below the anchor; when the viewport is
 * too tight the anchor is scrolled into view instead of flipping upwards.
 */
const Popover: React.FC<PopoverProps> = ({
  anchorRef,
  open,
  panelRef,
  className,
  children,
}) => {
  const [style, setStyle] = useState<PanelStyle | null>(null);

  useLayoutEffect(() => {
    if (!open) return;

    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;

      setStyle({
        left: rect.left,
        width: rect.width,
        top: rect.bottom + GAP,
        maxHeight: Math.min(MAX_PANEL_HEIGHT, Math.max(MIN_PANEL_HEIGHT, spaceBelow)),
      });
    };

    // Bring the field up the viewport when the space underneath cannot hold the
    // panel, so it can still open downwards without running off-screen.
    const anchor = anchorRef.current;
    if (anchor) {
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      if (spaceBelow < MIN_PANEL_HEIGHT) {
        anchor.scrollIntoView({ block: 'center' });
      }
    }

    update();
    // Capture phase so we also catch scrolls on inner overflow containers
    // (the onboarding form scrolls internally, not the page).
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef]);

  if (!open || !style) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: style.left,
        width: style.width,
        top: style.top,
        maxHeight: style.maxHeight,
        zIndex: 50,
      }}
      className={cn('overflow-auto', className)}
    >
      {children}
    </div>,
    document.body,
  );
};

export default Popover;
